// Табель: сетка на всю ширину раздела.
//
// ## Почему на всю ширину, а не деталью справа
//
// Полоса разделов 240 точек плюс колонка списка 384 — это 624 занятых. На ноутбуке 1366 детали
// остаётся около 740, а тридцать один день по 44 точки плюс фамилии, пост и итог — это больше
// 1300. Сетка в колонку детали не помещается физически, поэтому выбор объекта и месяца уезжает
// в её собственную шапку.
//
// ## Два режима, и оба настоящие
//
// Итоговой цифрой за месяц у заказчика ведутся 59 объектов из 60. Посменно — один. Поэтому по
// умолчанию открывается режим итоговых часов, а не сетка на тридцать один столбец.
//
// ## Закрытый период
//
// Ячейки остаются нажимаемыми и по нажатию говорят, кто и когда закрыл период и что делать
// дальше. Выключенная ячейка не доводит нажатие до обработчика: человек жмёт и не получает
// ничего, а со стороны это неотличимо от сломанной программы (правило 10).

import { useCallback, useMemo, useRef, useState } from 'react';

import { RiBuilding2Line, RiCalendarLine, RiLockLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { GridRowOut, ShiftType } from '@/shared/api/endpoints/timesheet';
import { НАЗВАНИЕ_ТИПА_СМЕНЫ } from '@/shared/api/endpoints/timesheet';
import { useAuthStore } from '@/shared/auth/store';
import { Подсказка } from '@/shared/hints/Подсказка';
import { DataGrid, type PastePlan } from '@/shared/grid';
import { can } from '@/shared/rbac/permissions';
import type { SearchState } from '@/shared/routing/search';
import { formatPeriodKey, formatPeriodTitle, parsePeriod, shiftPeriod } from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { Toolbar } from '@/shared/ui/toolbar';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { EmptyState, ErrorState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import * as SegmentedControl from '@/shared/ui/segmented-control';
import { SelectField, NO_VALUE } from '@/shared/ui/select-field';

import { FILTER_PERIOD, FILTER_SITE } from '../route';
import {
  useCopyDay,
  useFillByPlan,
  useGenerate,
  useSetTimesheetNote,
  useSetShiftType,
  useGrid,
  useOnShiftUnassigned,
  usePeriods,
  useSetCell,
  useSetCells,
  useSetEntryMode,
  useSetMonthTotals,
} from '../queries';

import { buildColumns, LETTER_TO_MARK, дниНедобора, состояниеДней } from './columns';
import { ПримечанияОбъекта } from './ПримечанияОбъекта';
import { formatDateOnly } from '@/shared/time/format';

import { ClosePeriodBar } from './ClosePeriodBar';
import { TotalsBar } from './TotalsBar';
import { КнопкаНеПопалиВТабель, ПРАВО_ПРОДЛЕВАТЬ } from './НеПопалиВТабель';

const EDIT_PERMISSION = 'core.timesheet.edit';

export interface TimesheetScreenProps {
  search: SearchState;
  onFilter: (key: string, value: string | null) => void;
}

export function TimesheetScreen({ search, onFilter }: TimesheetScreenProps) {
  const права = useAuthStore((state) => state.permissions);
  const можноПравить = can(права, EDIT_PERMISSION);

  const период = parsePeriod(search.filters[FILTER_PERIOD]);
  const выбранный = search.filters[FILTER_SITE] ?? null;

  const объекты = usePeriods(период);
  const список = объекты.data?.items ?? [];
  const объект = выбранный ?? список[0]?.site_id ?? null;

  const сетка = useGrid(объект, период);
  // Кто «не попал в табель» этого объекта: запрос стартует ВМЕСТЕ с сеткой, а не после неё.
  // Кнопка в полосе действий читает тот же ответ из кэша, и к первому кадру сетки число обычно
  // уже есть: кнопка, доехавшая позже сетки, могла бы перенести полосу и сдвинуть ячейки.
  useOnShiftUnassigned(объект, объект !== null && can(права, ПРАВО_ПРОДЛЕВАТЬ));
  const [сообщение, setСообщение] = useState<string | null>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <Toolbar>
        <div className='min-w-64'>
          <SelectField
            id='timesheet-site'
            label='Объект'
            value={объект ?? NO_VALUE}
            pending={объекты.isPending}
            failed={объекты.isError}
            failedError={объекты.error as ApiError | null}
            options={список.map((период) => ({
              value: период.site_id,
              label: период.site_name,
            }))}
            onChange={(значение) => onFilter(FILTER_SITE, значение === NO_VALUE ? null : значение)}
            placeholder='выберите объект'
            emptyNote='Объектов, доступных вам, нет. Их назначает администратор.'
          />
        </div>
        <PeriodStepper
          label='Месяц табеля'
          title={formatPeriodTitle(период)}
          onPrev={() => onFilter(FILTER_PERIOD, formatPeriodKey(shiftPeriod(период, -1)))}
          onNext={() => onFilter(FILTER_PERIOD, formatPeriodKey(shiftPeriod(период, 1)))}
        />
      </Toolbar>

      {/*
        Пока объект не выбран, запрос сетки выключен — значит у неё нет ни данных, ни ошибки, и
        общий каркас состояний честно уходит в «загрузку». Загрузка эта никогда не кончится:
        грузить нечего. Человек читал «объектов вам не назначено» и одновременно видел под этим
        текстом шевелящиеся скелеты — то есть решал, что надо подождать, и ждал. Поэтому обе
        ветки без объекта разбираются ЗДЕСЬ и до каркаса, а каркас включается только тогда,
        когда ему есть что показывать.
      */}
      {объект === null ? (
        <div className='p-5'>
          {объекты.error !== null ? (
            <ErrorState
              subject='список объектов табеля'
              error={объекты.error as never}
              onRetry={() => void объекты.refetch()}
            />
          ) : объекты.isPending ? (
            <div className='flex flex-col gap-2'>
              <Skeleton className='h-16 w-full rounded-lg' />
              <Skeleton className='h-64 w-full rounded-lg' />
            </div>
          ) : (
            <EmptyState
              icon={RiBuilding2Line}
              title='Объектов, доступных вам, нет'
              description={
                'Табель ведётся по объекту, а вам сейчас не назначен ни один. Объекты назначает ' +
                'администратор — попросите добавить вас, и табель появится здесь.'
              }
            />
          )}
        </div>
      ) : (
        <ScreenState
          data={сетка.data}
          error={сетка.error as never}
          subject='табель объекта'
          isEmpty={() => false}
          loading={
            <div className='flex flex-col gap-2 p-5'>
              <Skeleton className='h-16 w-full rounded-lg' />
              <Skeleton className='h-64 w-full rounded-lg' />
            </div>
          }
          empty={null}
          onRetry={() => void сетка.refetch()}
        >
          {(данные) => (
            <Табель
              данные={данные}
              период={период}
              siteId={объект}
              можноПравить={можноПравить}
              сообщение={сообщение}
              запрет={запрет}
              setСообщение={setСообщение}
              setЗапрет={setЗапрет}
            />
          )}
        </ScreenState>
      )}
    </main>
  );
}

function Табель({
  данные,
  период,
  siteId,
  можноПравить,
  сообщение,
  запрет,
  setСообщение,
  setЗапрет,
}: {
  данные: NonNullable<ReturnType<typeof useGrid>['data']>;
  период: ReturnType<typeof parsePeriod>;
  siteId: string;
  можноПравить: boolean;
  сообщение: string | null;
  запрет: string | null;
  setСообщение: (значение: string | null) => void;
  setЗапрет: (значение: string | null) => void;
}) {
  const ячейка = useSetCell(siteId, период);
  const пачка = useSetCells(siteId, период);
  const итоговые = useSetMonthTotals(siteId, период);
  const режим = useSetEntryMode(siteId, период);
  const график = useGenerate(siteId, период);
  const поГрафику = useFillByPlan(siteId, период);
  const повторДня = useCopyDay(siteId, период);
  const примечание = useSetTimesheetNote(siteId, период);
  const типСмены = useSetShiftType(siteId, период);

  /**
   * Тип смены в меню ячейки: на всю строку месяца.
   *
   * В меню, а не колонкой. Колонка потребовала бы у ячейки второго поведения — выбора из
   * списка вместо ввода часов, — а поведений у неё ровно четыре, и это правило. Меню же
   * открывается там, где человек и так стоит: на своей строке.
   */
  const пунктыТипаСмены = useCallback(
    (строка: GridRowOut) => {
      const выбрать = (тип: ShiftType | null) => () => {
        setСообщение(null);
        типСмены.mutate({ assignment_id: строка.assignment_id, shift_type: тип });
      };
      const пункты = (Object.keys(НАЗВАНИЕ_ТИПА_СМЕНЫ) as ShiftType[]).map((тип) => ({
        подпись:
          строка.shift_type === тип
            ? `${НАЗВАНИЕ_ТИПА_СМЕНЫ[тип]} — стоит сейчас`
            : НАЗВАНИЕ_ТИПА_СМЕНЫ[тип],
        выполнить: выбрать(тип),
      }));
      // «Снять тип» показывается только когда снимать есть что: пункт, который ничего не
      // делает, человек всё равно нажмёт — и решит, что программа его не поняла.
      if (строка.shift_type !== null || строка.shift_type_mixed) {
        пункты.push({ подпись: 'Снять тип смены', выполнить: выбрать(null) });
      }
      return пункты;
    },
    [типСмены, setСообщение],
  );

  const [план, setПлан] = useState<PastePlan<GridRowOut> | null>(null);
  const [сменаРежима, setСменаРежима] = useState<'by_shift' | 'month_total' | null>(null);

  const посменно = данные.period.entry_mode === 'by_shift';
  const правится = можноПравить && данные.can_edit;
  // Пустой табель — это не «плохой» табель, а начало месяца. Но обычное действие на нём другое,
  // чем на заполненном, и полоса действий обязана это показывать весом кнопки.
  const пустойТабель = !данные.period.totals.has_any_shift;

  // Где именно объект не закрыт. Считается один раз на весь табель и раздаётся колонкам:
  // тридцать колонок, складывающих тринадцать строк каждая, — это тридцать проходов по сетке
  // на каждую перерисовку. В режиме «итог за месяц» дней нет вовсе и считать нечего.
  const состояниеПоДням = useMemo(
    () =>
      посменно
        ? состояниеДней(данные.rows, данные.days, данные.period.totals.norm_per_day, {
            // Пустой табель не красим вовсе, и будущие дни тоже: «пост не закрыт» про день,
            // который ещё не наступил, — это не находка, а шум, от которого перестают
            // смотреть на красное вообще.
            естьСмены: данные.period.totals.has_any_shift,
          })
        : new Map<string, never>(),
    [
      посменно,
      данные.rows,
      данные.days,
      данные.period.totals.norm_per_day,
      данные.period.totals.has_any_shift,
    ],
  );
  const проблемныеДни = useMemo(() => дниНедобора(состояниеПоДням), [состояниеПоДням]);
  // На какой из них смотрим сейчас. Нажатие на «Недобор» ведёт к следующему по кругу: дней
  // бывает несколько, и «показать первый» отвечает только на первый вопрос из трёх.
  const [кудаСмотрим, setКудаСмотрим] = useState(0);
  const сетка = useRef<HTMLDivElement>(null);

  /**
   * Показать следующий день, где объект не закрыт.
   *
   * Прокручиваем к заголовку столбца, а не к ячейке: недобор — свойство ДНЯ, и приводить к
   * чьей-то строке значило бы назвать виновного, которого из данных не видно. Дней бывает
   * несколько, поэтому ходим по кругу, а подпись у кнопки говорит, который сейчас.
   */
  function показатьПроблемныйДень() {
    if (проблемныеДни.length === 0) {
      return;
    }
    const день = проблемныеДни[кудаСмотрим % проблемныеДни.length];
    setКудаСмотрим((было) => (было + 1) % проблемныеДни.length);
    if (день === undefined) {
      return;
    }
    const заголовок = сетка.current?.querySelector(`th[data-column="${CSS.escape(день)}"]`);
    заголовок?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  const колонки = useMemo(
    () =>
      buildColumns({
        days: данные.days,
        canEdit: правится,
        blockedReason: правится
          ? null
          : (данные.blocked_reason ??
            'Права на ведение табеля у вас нет. Его ведёт табельщик или финансовый отдел.'),
        monthTotalMode: !посменно,
        состояние: состояниеПоДням,
      }),
    [данные.days, данные.blocked_reason, правится, посменно, состояниеПоДням],
  );

  function сохранить(строка: GridRowOut, колонка: string, значение: string) {
    setСообщение(null);
    if (колонка === 'month-total') {
      итоговые.mutate([
        { assignment_id: строка.assignment_id, hours: значение === '' ? null : значение },
      ]);
      return;
    }
    ячейка.mutate(вЯчейку(строка, колонка, значение));
  }

  /**
   * Значение из ячейки в правку. Буква ставит ОТМЕТКУ, а не часы: так табель и читают глазами,
   * и другого способа проставить невыход на экране нет.
   *
   * Пустое значение при уже стоящей отметке отметку СОХРАНЯЕТ. Иначе табельщик, чистя случайно
   * набранную цифру, молча превращал бы невыход в отработанную смену — и человек получил бы
   * деньги за день, когда его не было.
   */
  function вЯчейку(строка: GridRowOut, день: string, значение: string) {
    const отметкаБуквой = LETTER_TO_MARK[значение.trim()];
    if (отметкаБуквой !== undefined) {
      return {
        assignment_id: строка.assignment_id,
        work_date: день,
        hours: null,
        mark: отметкаБуквой,
      };
    }
    const прежняя = строка.cells.find((я) => я.work_date === день);
    const держимОтметку =
      значение.trim() === '' && прежняя !== undefined && прежняя.mark !== 'worked';
    return {
      assignment_id: строка.assignment_id,
      work_date: день,
      hours: значение.trim() === '' ? null : значение.trim(),
      mark: держимОтметку ? прежняя.mark : ('worked' as const),
    };
  }

  // Правило 6: «ляжет N, отклонено M и почему» показывается ДО применения. Раньше разбор
  // печатался одновременно с отправкой — то есть человек читал итог уже свершившегося.
  function вставить(план: PastePlan<GridRowOut>) {
    setСообщение(null);
    setПлан(план);
  }

  function применитьВставку() {
    const текущий = план;
    if (текущий === null || текущий.accepted.length === 0) {
      // Молчаливый выход отсюда — то же самое, что серая кнопка: человек нажал и не получил
      // ничего. Разница только в том, что снаружи это выглядит ещё хуже, потому что кнопка
      // выглядела рабочей.
      setЗапрет(
        'Из вставленного не ложится ничего. Причины перечислены слева: поправьте кусок в книге и вставьте заново.',
      );
      return;
    }
    setПлан(null);
    if (!посменно) {
      итоговые.mutate(
        текущий.accepted.map((пункт) => ({
          assignment_id: пункт.row.assignment_id,
          hours: пункт.value === '' ? null : пункт.value,
        })),
      );
      return;
    }
    // ОДИН запрос на всю вставку. По запросу на ячейку месяц на тринадцать человек — это
    // четыреста обращений подряд и четыреста перерисовок сетки.
    пачка.mutate(текущий.accepted.map((пункт) => вЯчейку(пункт.row, пункт.columnId, пункт.value)));
  }

  const якорьСпособа = useRef<HTMLDivElement>(null);

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <TotalsBar
        totals={данные.period.totals}
        проблемныеДни={проблемныеДни}
        наКакойСмотрим={кудаСмотрим}
        показатьДень={проблемныеДни.length === 0 ? undefined : показатьПроблемныйДень}
      />

      {/* Слева — ЧЕМ СМОТРИМ, справа — ЧТО ДЕЛАЕМ.
          
          До 04.09.2026 переключатель представления и три кнопки действия стояли одним рядом
          подряд, без единого промежутка: глазу не за что было зацепиться, чтобы отличить «это
          меняет вид» от «это меняет данные». Разделяет их не линия и не заголовок, а пустота:
          она читается быстрее любого разделителя и ничего не добавляет на экран. */}
      <div className='flex flex-wrap items-center gap-2 border-b border-stroke-soft-200 px-5 py-2 узко:px-4'>
        <div ref={якорьСпособа}>
          <Подсказка урок='способ-табеля' якорь={якорьСпособа} />
        </div>
        <SegmentedControl.Root
          value={посменно ? 'by_shift' : 'month_total'}
          onValueChange={(значение) => {
            setСообщение(null);
            if (!правится) {
              setЗапрет(
                данные.blocked_reason ??
                  'Права на ведение табеля у вас нет. Его ведёт табельщик или финансовый отдел — попросите их, либо администратора выдать вам право.',
              );
              return;
            }
            // Переключение ГАСИТ часы чужого режима и вернуть их нечем. Спрашиваем заранее и
            // называем, сколько именно погаснет (правило 4: подтверждение показывает итог).
            setСменаРежима(значение as 'by_shift' | 'month_total');
          }}
        >
          <SegmentedControl.List>
            <SegmentedControl.Trigger value='month_total'>Итог за месяц</SegmentedControl.Trigger>
            <SegmentedControl.Trigger value='by_shift'>По сменам</SegmentedControl.Trigger>
          </SegmentedControl.List>
        </SegmentedControl.Root>

        {/* Пустота, разводящая вид и действия. Растёт, сколько бы кнопок ни добавили. */}
        <div className='flex-1' />

        {посменно && (
          <>
            {/* Пока в табеле не отмечено ни одной смены, обычное действие здесь одно —
                построить график. Оно и выделено. До 25.08.2026 все три кнопки шли одним весом,
                а сильнее всех выглядело «Закрыть период»: на пустом табеле экран предлагал
                закрыть месяц, в котором никто ещё не отмечен. */}
            <Button.Root
              // Выделяем цветом только тому, кто этим действием пользуется. Кнопка остаётся
              // на месте и по нажатию объясняет отказ (правило 10) — но быть САМЫМ громким
              // на экране действию, которого человеку нельзя, незачем. Найдено репетицией
              // 15.09.2026: у полевого руководителя, который табель только читает, «Создать
              // график» горело первым цветом на весь экран.
              variant={пустойТабель && правится ? 'primary' : 'neutral'}
              mode={пустойТабель && правится ? 'filled' : 'stroke'}
              size='xsmall'
              onClick={() => {
                if (!правится) {
                  setЗапрет(
                    данные.blocked_reason ??
                      'Права на ведение табеля у вас нет. Его ведёт табельщик или финансовый отдел — попросите их, либо администратора выдать вам право.',
                  );
                  return;
                }
                setСообщение(null);
                график.mutate(undefined, {
                  onSuccess: (итог) =>
                    setСообщение(
                      `Заведено дней по графику: ${итог.created}` +
                        (итог.skipped > 0 ? `, уже было заполнено: ${итог.skipped}` : '') +
                        (итог.rows_without_schedule.length > 0
                          ? `. График не построен: ${итог.rows_without_schedule.join('; ')} — у поста не задан режим работы`
                          : ''),
                    ),
                });
              }}
            >
              {график.isPending ? 'Строим…' : 'Создать график'}
            </Button.Root>
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => {
                if (!правится) {
                  setЗапрет(
                    данные.blocked_reason ??
                      'Права на ведение табеля у вас нет. Его ведёт табельщик или финансовый отдел — попросите их, либо администратора выдать вам право.',
                  );
                  return;
                }
                setСообщение(null);
                поГрафику.mutate(undefined, {
                  onSuccess: (итог) =>
                    setСообщение(
                      итог.filled === 0
                        ? 'Проставлять было нечего: часы уже стоят везде, где есть график.'
                        : `Часы проставлены по графику: ${итог.filled}. Исключения не тронуты.`,
                    ),
                });
              }}
            >
              {поГрафику.isPending ? 'Отмечаем…' : 'Отметить по графику'}
            </Button.Root>
            {/* Повтор дня — обычный способ вести табель, а не запасной. График строится по
                режиму поста, а он задан не у всех; вставка прямоугольника закрывает случай
                «данные уже где-то набраны». Повтор закрывает третий и самый частый: пост
                стоит одинаково, и месяц отличается от первого дня только исключениями. */}
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => {
                if (!правится) {
                  setЗапрет(
                    данные.blocked_reason ??
                      'Права на ведение табеля у вас нет. Его ведёт табельщик или финансовый отдел — попросите их, либо администратора выдать вам право.',
                  );
                  return;
                }
                setСообщение(null);
                повторДня.mutate(undefined, {
                  onSuccess: (итог) =>
                    setСообщение(
                      итог.source_date === null
                        ? 'Повторять нечего: в этом месяце ещё ничего не отмечено.'
                        : `Повторили ${formatDateOnly(итог.source_date)} до конца месяца: ` +
                            `заполнено ${итог.filled} по ${итог.rows} людям` +
                            (итог.skipped > 0 ? `, пропущено уже отмеченных ${итог.skipped}` : '') +
                            '.',
                    ),
                });
              }}
            >
              {повторДня.isPending ? 'Повторяем…' : 'Повторить день'}
            </Button.Root>
          </>
        )}

        {/* Люди «на вахте», у которых назначение кончилось раньше конца месяца: их нет в этой
            сетке, и продлить их можно отсюда же, пачкой (19.10.2026). В той же полосе, а не
            новой строкой над сеткой: строка, приехавшая позже сетки, сдвинула бы ячейку под
            курсором. */}
        <КнопкаНеПопалиВТабель siteId={siteId} месяц={период} />

        <ClosePeriodBar
          период={данные.period}
          siteId={siteId}
          месяц={период}
          строк={данные.rows.length}
          главная={!пустойТабель}
        />
      </div>

      {данные.blocked_reason !== null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-2'>
          <Alert.Icon as={RiLockLine} />
          {данные.blocked_reason}
        </Alert.Root>
      )}
      {сообщение !== null && (
        <p className='border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4 text-paragraph-sm text-text-sub-600'>
          {сообщение}
        </p>
      )}
      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-2'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      {ячейка.error !== null && (
        <div className='px-5 py-2 узко:px-4'>
          <ErrorAlert error={ячейка.error as never} />
        </div>
      )}
      {[итоговые.error, пачка.error, график.error, поГрафику.error, режим.error]
        .filter((ошибка) => ошибка !== null)
        .map((ошибка, номер) => (
          <div key={номер} className='px-5 py-2 узко:px-4'>
            <ErrorAlert error={ошибка as never} />
          </div>
        ))}

      {план !== null && (
        <div className='flex flex-wrap items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
          <span className='text-paragraph-sm text-text-sub-600'>{план.summary}</span>
          <Button.Root
            variant='primary'
            mode='filled'
            size='xxsmall'
            // Кнопка НЕ сереет: выключенная не доводит нажатие до обработчика, и табельщик,
            // вставивший не тот кусок из книги, не получает в ответ ничего. Причина живёт в
            // обработчике — так же, как в официальном контуре.
            onClick={применитьВставку}
          >
            Применить
          </Button.Root>
          <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={() => setПлан(null)}>
            Отменить
          </Button.Root>
        </div>
      )}

      {сменаРежима !== null && (
        <div className='flex flex-wrap items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'>
          <span className='text-paragraph-sm text-text-sub-600'>
            {сменаРежима === 'month_total'
              ? `Переход на итог за месяц погасит проставленные по дням часы: ${заполненныхЯчеек(данные)}. Вернуть их будет нечем, график при этом сохранится.`
              : `Переход на посменный режим погасит итоговые цифры за месяц: ${заполненныхИтогов(данные)}. Вернуть их будет нечем.`}
          </span>
          <Button.Root
            variant='error'
            mode='stroke'
            size='xxsmall'
            onClick={() => {
              const режимДляСмены = сменаРежима;
              setСменаРежима(null);
              режим.mutate(режимДляСмены);
            }}
          >
            {режим.isPending ? 'Переключаем…' : 'Переключить'}
          </Button.Root>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => setСменаРежима(null)}
          >
            Оставить как есть
          </Button.Root>
        </div>
      )}

      <div ref={сетка} className='min-h-0 flex-1'>
        <DataGrid<GridRowOut>
          rows={данные.rows}
          columns={колонки}
          rowKey={(строка) => строка.assignment_id}
          rowLabel={(строка) => строка.employee_name}
          onCommit={правится ? сохранить : undefined}
          onPaste={правится ? вставить : undefined}
          onBlocked={setЗапрет}
          menuExtras={правится ? пунктыТипаСмены : undefined}
          validate={проверитьЧасы}
          emptyText='На этом объекте за выбранный месяц никто не назначен. Поставьте людей в разделе «Люди».'
        />
      </div>

      {/* Под сеткой, ровно как просил заказчик: «нужно поле под графиками для комментариев».
          Не в шапке и не панелью — примечание читают вместе с цифрами, которые оно объясняет,
          а не вместо них. */}
      <ПримечанияОбъекта
        данные={данные}
        правится={правится}
        запретПричина={данные.blocked_reason}
        запись={примечание}
      />
    </div>
  );
}

/** Сколько посменных ячеек несут часы: столько погаснет при переходе на итог за месяц. */
function заполненныхЯчеек(данные: { rows: GridRowOut[] }): string {
  const сколько = данные.rows.reduce(
    (итог, строка) => итог + строка.cells.filter((я) => я.hours !== null).length,
    0,
  );
  return сколько === 0 ? 'таких нет' : `${сколько}`;
}

/** Сколько строк несут итог за месяц: столько погаснет при переходе на посменный режим. */
function заполненныхИтогов(данные: { rows: GridRowOut[] }): string {
  const сколько = данные.rows.filter((строка) => строка.month_total_hours !== null).length;
  return сколько === 0 ? 'таких нет' : `${сколько}`;
}

/**
 * Проверка часов. Кратность длительности смены НЕ проверяется: из 268 строк майской книги
 * заказчика 36 не делятся на 12 — восемь часов у Дремина, пятьдесят четыре у Сорокина.
 */
function проверитьЧасы(значение: string): string | null {
  if (значение === '') {
    return null;
  }
  // Буква — это отметка: Н невыход, Б больничный, О отгул. Проверять её как число нельзя.
  if (LETTER_TO_MARK[значение.trim()] !== undefined) {
    return null;
  }
  const число = Number(значение.replace(',', '.'));
  if (!Number.isFinite(число)) {
    return 'Ожидались часы числом (12, 7,5) либо отметка буквой: Н невыход, Б больничный, О отгул.';
  }
  if (число < 0) {
    return 'Часы не бывают отрицательными.';
  }
  if (число > 744) {
    return 'В месяце не бывает больше 744 часов.';
  }
  return null;
}

export const TIMESHEET_ICON = RiCalendarLine;
